import { Clock, Copy, ExternalLink, Info, QrCode, TriangleAlert } from "lucide-react";
import { useId, useMemo, type ReactNode } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { useAddressQr } from "@/shared/hooks/useAddressQr";
import { useQuoteCountdown } from "@/shared/hooks/useQuoteCountdown";
import useTranslations from "@/shared/lang/useTranslations";

import { useAmountFormatter } from "../../hooks/useAmountFormatter";
import { useCopyValue } from "../../hooks/useCopyValue";
import { useCryptoSwapLabels } from "../../hooks/useCryptoSwapLabels";
import type { OtcSellOrder } from "../../types/cryptoSell.types";
import { depositAddressDeadlineMs, usableDepositAddress } from "../../utils/cryptoSellStatus";
import { chunkAddress } from "../../utils/cryptoSwapAddress";
import { formatCountdown } from "../../utils/cryptoSwapFormat";
import { AssetAvatar, MonoValue } from "../shared/CryptoSwapBits";
import { NetworkIcon } from "../shared/NetworkIcon";

export interface DepositSlipProps {
  sale: OtcSellOrder;
}

/**
 * What to send, where, and how — the only screen a deposit address ever appears on. Laid out like the quote
 * ticket it came from: the dark slip carries the three values to copy, in the order a wallet asks for them.
 */
export function DepositSlip({ sale }: DepositSlipProps) {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const format = useAmountFormatter();
  const copy = useCopyValue();
  const { deposit } = sale;
  const rule = labels.networkRule(deposit.network);
  const networkLabel = labels.network(deposit.network);
  const amountText = format(deposit.amount, deposit.currency);
  const titleId = useId();
  const deadlineMs = depositAddressDeadlineMs(deposit);
  const deadlineIso = useMemo(
    () => (deadlineMs === null ? null : new Date(deadlineMs).toISOString()),
    [deadlineMs],
  );
  const { isExpired, timeRemaining } = useQuoteCountdown(deadlineIso);
  const address = usableDepositAddress(deposit, deadlineMs === null ? 0 : deadlineMs - timeRemaining);
  const qr = useAddressQr(address ?? "");
  const { head, middle, tail } = chunkAddress(address ?? "");

  return (
    <section
      aria-labelledby={titleId}
      className="overflow-hidden rounded-3xl border border-border shadow-[var(--shadow-elev)]"
    >
      <div className="dark bg-ticket text-ticket-foreground">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 pt-6">
          <h2
            id={titleId}
            className="text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase"
          >
            {t("sell_deposit_title")}
          </h2>
          <span className="flex flex-wrap items-center gap-2">
            {address && (
              <span
                role="timer"
                className="inline-flex items-center gap-1.5 rounded-full border border-warning/40 px-2.5 py-0.5 font-mono text-xs font-medium text-warning"
              >
                <Clock aria-hidden="true" className="size-3" />
                {t("sell_deposit_time_left", { time: formatCountdown(timeRemaining) })}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-0.5 font-mono text-xs font-medium text-foreground">
              {rule?.standard ?? deposit.network}
            </span>
          </span>
        </div>

        {!address ? (
          <div className="p-6">
            {deadlineMs !== null && isExpired ? (
              <ColocaAlert
                variant="destructive"
                icon={TriangleAlert}
                title={t("sell_deposit_expired_title")}
                text={t("sell_deposit_expired_text")}
              />
            ) : (
              <ColocaAlert
                variant="secondary"
                icon={Info}
                title={t("sell_deposit_unavailable_title")}
                text={t("sell_deposit_unavailable_text")}
              />
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-6 p-6 sm:flex-row sm:items-start">
            <div className="mx-auto shrink-0 sm:mx-0">
              <div className="size-44 overflow-hidden rounded-2xl bg-white p-2.5">
                {qr.pending ? (
                  <ColocaSkeleton className="size-full rounded-xl" />
                ) : qr.src ? (
                  <img
                    src={qr.src}
                    alt={t("sell_deposit_qr_alt", { network: networkLabel })}
                    className="size-full object-contain"
                  />
                ) : (
                  <div className="flex size-full items-center justify-center text-neutral-400">
                    <QrCode aria-hidden="true" className="size-10" />
                  </div>
                )}
              </div>
            </div>

            <div
              aria-hidden="true"
              className="border-t border-dashed border-border sm:self-stretch sm:border-t-0 sm:border-l"
            />

            <dl className="m-0 flex min-w-0 flex-1 flex-col gap-5">
              <SlipField label={t("sell_deposit_amount")}>
                <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                  <AssetAvatar asset={deposit.currency} kind="ONCHAIN" size="md" />
                  <MonoValue className="text-4xl leading-none font-semibold tracking-tight">
                    {amountText}
                  </MonoValue>
                  <span className="font-mono text-base font-medium text-success-container-foreground">
                    {deposit.currency}
                  </span>
                </span>
                <ColocaButton
                  variant="secondary"
                  size="sm"
                  icon={Copy}
                  className="self-start"
                  onClick={() => void copy(deposit.amount, t("copy_deposit_amount"))}
                >
                  {t("sell_deposit_copy_amount")}
                </ColocaButton>
              </SlipField>

              <SlipField label={t("sell_deposit_address")}>
                <span className="sr-only">{address}</span>
                <p aria-hidden="true" className="m-0 flex flex-wrap gap-x-2.5 gap-y-1 font-mono text-[15px]">
                  <span className="rounded bg-primary/15 px-1.5 font-semibold text-foreground">{head}</span>
                  {middle.map((group, index) => (
                    <span key={`${group}-${index}`} className="text-muted-foreground">
                      {group}
                    </span>
                  ))}
                  <span className="rounded bg-primary/15 px-1.5 font-semibold text-foreground">{tail}</span>
                </p>
                <ColocaButton
                  variant="secondary"
                  size="sm"
                  icon={Copy}
                  className="self-start"
                  onClick={() => void copy(address, t("copy_deposit_address"))}
                >
                  {t("sell_deposit_copy_address")}
                </ColocaButton>
              </SlipField>

              <SlipField label={t("sell_deposit_network")}>
                <span className="inline-flex items-center gap-2 text-[15px] font-semibold text-foreground">
                  <NetworkIcon network={deposit.network} size={22} />
                  {networkLabel}
                </span>
                {rule && (
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                    {t("sell_deposit_contract", { asset: deposit.currency })}
                    <MonoValue breakAll className="text-foreground">
                      {rule.usdtContract}
                    </MonoValue>
                    <a
                      href={rule.contractUrl(rule.usdtContract)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                    >
                      {t("sell_view_on_explorer", { explorer: rule.explorer })}
                      <ExternalLink aria-hidden="true" className="size-3" />
                    </a>
                  </span>
                )}
              </SlipField>
            </dl>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-4 bg-card p-6">
        <h3 className="text-sm font-semibold text-foreground">{t("sell_deposit_steps_title")}</h3>
        <ol className="m-0 flex list-none flex-col gap-3 p-0">
          {[
            t("sell_deposit_step_network", { network: networkLabel }),
            t("sell_deposit_step_send", { asset: deposit.currency }),
            t("sell_deposit_step_amount", {
              amount: `${amountText} ${deposit.currency}`,
              gas: rule?.gasAsset ?? "",
            }),
            t("sell_deposit_step_wait"),
          ].map((text, index) => (
            <li key={index} className="flex gap-3">
              <span
                aria-hidden="true"
                className="flex size-6 shrink-0 items-center justify-center rounded-full border border-border font-mono text-xs font-semibold text-foreground"
              >
                {index + 1}
              </span>
              <span className="pt-0.5 text-sm leading-relaxed text-foreground">{text}</span>
            </li>
          ))}
        </ol>
        <ColocaAlert
          variant="warning"
          icon={TriangleAlert}
          text={t("sell_deposit_warning", { asset: deposit.currency, network: networkLabel })}
        />
        <p className="text-[13px] leading-relaxed text-muted-foreground">{t("sell_deposit_window")}</p>
      </div>
    </section>
  );
}

function SlipField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <dt className="text-[13px] text-muted-foreground">{label}</dt>
      <dd className="m-0 flex flex-col gap-2.5">{children}</dd>
    </div>
  );
}
